
import AppHeader from "@/components/AppHeader.vue";
import Error from "@/components/Error.vue";
import Page from "@/components/Page.vue";
import User from "@/views/User/User.vue";
import useError from "@/composables/useError";
import {
  PageParamsInputType,
  useGetUsersPaginationQuery,
  UserType,
} from "@/graphql/types";
import { useResult } from "@vue/apollo-composable";
import gql from "graphql-tag";
import {
  defineComponent,
  ref,
  onActivated,
  onMounted,
  watch,
  onUnmounted,
  computed,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import Pagination from "@/components/pagination/Pagination.vue";
import { PageState } from "primevue/paginator";
import useUserSidebar from "@/views/User/useUserSidebar";
import useUser from "@/composables/useUser";
import {
  displayUserCreationDate,
  totalDaysSinceLastLogin,
} from "@/composables/utils";

// Query
gql`
  query GetUsersPagination($pageParams: PageParamsInputType!) {
    usersPagination(pageParams: $pageParams) {
      items {
        userId
        firstname
        lastname
        email
        mitID
        kombitID
        eID
        administrator
        disabled
        lastLogin
        creationDate
        organization {
          organizationId
          name
        }
      }
      total
    }
  }
`;

export default defineComponent({
  inheritAttrs: false,
  components: {
    AppHeader,
    Page,
    Error,
    Pagination,
    User,
  },
  name: "users",
  setup() {
    const route = useRoute();
    const userSidebar = useUserSidebar();
    const router = useRouter();
    const pageParams = ref<PageParamsInputType>({
      pageSize: 10,
      currentPage: 1,
      filter: "",
    });

    // If currentPage is in the query, use it
    if (route.query.currentPage) {
      const page = parseInt(route.query.currentPage as string, 10);
      if (!isNaN(page) && page > 0) {
        pageParams.value.currentPage = page;
      }
    }

    // Search input and debounce
    const searchText = ref("");
    let searchTimeout: ReturnType<typeof setTimeout> | null = null;

    watch(searchText, (val) => {
      if (searchTimeout) clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        pageParams.value.filter = val;
        pageParams.value.currentPage = 1;
        // Remove 'search' from query if empty, otherwise set it
        const newQuery: Record<string, any> = {
          ...route.query,
          currentPage: 1,
        };
        if (val && val.trim() !== "") {
          newQuery.search = val;
        } else {
          delete newQuery.search;
        }
        router.replace({ query: newQuery });
        refetch({ pageParams: pageParams.value });
      }, 500); // 500ms debounce
    });

    // If search is in the query, use it
    if (route.query.search) {
      searchText.value = String(route.query.search);
      pageParams.value.filter = searchText.value;
    }

    const { user } = useUser();

    // Role-based computed properties
    const canManageUsers = computed(
      () =>
        user.value?.isSuperAdmin ||
        user.value?.isUserAdmin ||
        user.value?.isAdmin ||
        user.value?.isApplicationAdmin ||
        false
    );

    const { result, loading, error, refetch } = useGetUsersPaginationQuery({
      pageParams: pageParams.value,
    });

    function onPageChange(event: PageState) {
      pageParams.value.pageSize = event.rows;
      pageParams.value.currentPage = event.page + 1;
      // Update the route query with the new page and search
      const query: Record<string, any> = {
        ...route.query,
        currentPage: pageParams.value.currentPage,
      };
      if (searchText.value && searchText.value.trim() !== "") {
        query.search = searchText.value;
      } else {
        delete query.search;
      }
      router.replace({ query });
      refetch({ pageParams: pageParams.value });
    }

    function onUserDeleted() {
      // Refetch the users list to reflect the deletion
      refetch({ pageParams: pageParams.value });
    }

    // Always refetch users when the page is activated or mounted
    onActivated(() => {
      refetch({ pageParams: pageParams.value });
    });
    onMounted(() => {
      refetch({ pageParams: pageParams.value });
    });

    onUnmounted(() => {
      if (searchTimeout) clearTimeout(searchTimeout);
    });

    return {
      user,
      canManageUsers,
      users: useResult(result),
      error: useError(error),
      loading,
      onPageChange,
      onUserDeleted,
      pageParams,
      searchText,
      userSidebar,
      totalDaysSinceLastLogin,
      displayUserCreationDate,
    };
  },
});
